import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Calendar, Trash2, Copy, Check, X } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ui/image-upload";
import { useAdminCopy } from "@/contexts/AdminCopyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FRONTEND_BASE = window.location.origin;

function emptySlot() {
  return { slot_id: `tmp_${Math.random().toString(36).slice(2, 10)}`, start_iso: "", end_iso: "", label: "" };
}

/**
 * HostInvitesPanel
 *
 * Amayah-style: build an invite, attach availability slots, copy the link,
 * paste it into a DM (Instagram, iMessage, text, email). The invitee
 * picks a slot, confirms email/phone, lands on /c/{card_id}.
 *
 * No Acuity required. This is the plug-and-play native booking primitive.
 */
export default function HostInvitesPanel({ eventId }) {
  const t = useAdminCopy();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [form, setForm] = useState({
    sent_to_name: "",
    sent_to_handle: "",
    service_label: "",
    note: "",
    slots: [emptySlot()],
    // Host pre-fill (editable on the client side - just a head start)
    prefill_first_name: "",
    prefill_last_name: "",
    prefill_email: "",
    prefill_phone: "",
    prefill_inspo_images: [],  // array of uploaded image URLs (real uploads, not pasted URLs)
  });
  const [draftInspoUrl, setDraftInspoUrl] = useState("");

  const fetchInvites = async () => {
    try {
      const res = await fetch(`${API}/host/invites?event_id=${eventId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch (_e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (eventId) fetchInvites();
  }, [eventId]); // eslint-disable-line

  const addSlot = () => setForm({ ...form, slots: [...form.slots, emptySlot()] });
  const removeSlot = (idx) => setForm({ ...form, slots: form.slots.filter((_, i) => i !== idx) });
  const updateSlot = (idx, patch) => {
    const next = [...form.slots];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, slots: next });
  };

  const handleCreate = async () => {
    const validSlots = form.slots.filter((s) => s.start_iso);
    if (validSlots.length === 0) {
      toast.error("Add at least one available time slot");
      return;
    }
    const payload = {
      event_id: eventId,
      service_label: form.service_label,
      note: form.note,
      sent_to_name: form.sent_to_name,
      sent_to_handle: form.sent_to_handle,
      prefill: {
        first_name: form.prefill_first_name,
        last_name: form.prefill_last_name,
        email: form.prefill_email,
        phone: form.prefill_phone,
        inspo_urls: form.prefill_inspo_images || [],
      },
      slots: validSlots.map((s) => ({
        start_iso: new Date(s.start_iso).toISOString(),
        end_iso: s.end_iso ? new Date(s.end_iso).toISOString() : null,
        label: s.label,
      })),
    };
    try {
      const res = await fetch(`${API}/host/invites/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        toast.error("Could not create invite");
        return;
      }
      toast.success("Invite created. Copy the link and DM it.");
      setShowForm(false);
      setForm({
        sent_to_name: "", sent_to_handle: "", service_label: "", note: "",
        slots: [emptySlot()],
        prefill_first_name: "", prefill_last_name: "",
        prefill_email: "", prefill_phone: "", prefill_inspo_images: [],
      });
      setDraftInspoUrl("");
      fetchInvites();
    } catch (_e) {
      toast.error("Network issue");
    }
  };

  const handleCopy = (token) => {
    const url = `${FRONTEND_BASE}/rsvp/invite/${token}`;
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 1500);
    toast.success("Invite link copied");
  };

  const handleDelete = async (invite_id) => {
    if (!window.confirm("Cancel this invite?")) return;
    try {
      const res = await fetch(`${API}/host/invites/${invite_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (res.ok) {
        toast.success("Invite cancelled");
        fetchInvites();
      }
    } catch (_e) {
      toast.error("Could not cancel invite");
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5" data-testid="host-invites-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-medium text-stone-900 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Personal Invites
          </h3>
          <p className="text-xs text-stone-500 mt-1">
            Send a custom availability invite by DM, text, or email. No public calendar required.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(!showForm)}
          className="rounded-full"
          data-testid="host-invites-new-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New invite
        </Button>
      </div>

      {showForm && (
        <div className="mb-4 p-4 bg-stone-50 rounded-xl border border-stone-200 space-y-3" data-testid="host-invites-form">
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="Their name (optional)"
              value={form.sent_to_name}
              onChange={(e) => setForm({ ...form, sent_to_name: e.target.value })}
              className="rounded-xl text-sm h-9"
              data-testid="host-invites-sent-to-name"
            />
            <Input
              placeholder="Their @handle (optional)"
              value={form.sent_to_handle}
              onChange={(e) => setForm({ ...form, sent_to_handle: e.target.value })}
              className="rounded-xl text-sm h-9"
              data-testid="host-invites-sent-to-handle"
            />
          </div>
          <Input
            placeholder="Service (e.g., Loc Retwist, In Studio Appt)"
            value={form.service_label}
            onChange={(e) => setForm({ ...form, service_label: e.target.value })}
            className="rounded-xl text-sm h-9"
            data-testid="host-invites-service"
          />
          <Textarea
            placeholder="Personal note (optional)"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="rounded-xl text-sm min-h-16"
            data-testid="host-invites-note"
          />

          {/* Host pre-fill: any field the client would fill, the host can fill
              ahead of time. All values stay editable on the client view. */}
          <div className="pt-2 border-t border-stone-200">
            <p className="text-xs font-medium text-stone-700 mb-2">Pre-fill what you already know (optional)</p>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <Input
                placeholder="First name"
                value={form.prefill_first_name}
                onChange={(e) => setForm({ ...form, prefill_first_name: e.target.value })}
                className="rounded-xl text-sm h-9"
                data-testid="host-invites-prefill-first"
              />
              <Input
                placeholder="Last name"
                value={form.prefill_last_name}
                onChange={(e) => setForm({ ...form, prefill_last_name: e.target.value })}
                className="rounded-xl text-sm h-9"
                data-testid="host-invites-prefill-last"
              />
            </div>
            <Input
              placeholder="Email"
              type="email"
              value={form.prefill_email}
              onChange={(e) => setForm({ ...form, prefill_email: e.target.value })}
              className="rounded-xl text-sm h-9 mb-2"
              data-testid="host-invites-prefill-email"
            />
            <Input
              placeholder="Phone"
              type="tel"
              value={form.prefill_phone}
              onChange={(e) => setForm({ ...form, prefill_phone: e.target.value })}
              className="rounded-xl text-sm h-9 mb-2"
              data-testid="host-invites-prefill-phone"
            />
            <div>
              <p className="text-[11px] text-stone-600 mb-1">Inspo images to attach (optional)</p>
              {form.prefill_inspo_images.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2" data-testid="host-invites-inspo-thumbs">
                  {form.prefill_inspo_images.map((url, idx) => (
                    <div key={idx} className="relative w-12 h-12 rounded-lg overflow-hidden border border-stone-200">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => {
                          if (!window.confirm("Are you sure you want to remove this image?")) return;
                          setForm((f) => ({ ...f, prefill_inspo_images: f.prefill_inspo_images.filter((_, i) => i !== idx) }));
                        }}
                        className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white rounded-bl-md flex items-center justify-center"
                        data-testid={`host-invites-inspo-remove-${idx}`}
                      >
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <ImageUpload
                value={draftInspoUrl}
                onChange={(url) => {
                  if (url) {
                    setForm((f) => ({ ...f, prefill_inspo_images: [...f.prefill_inspo_images, url] }));
                    setDraftInspoUrl("");
                  }
                }}
                previewSize="medium"
                aspectRatio="square"
                description="Upload from your device. Add as many as you want."
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-stone-700 mb-2">Available slots</p>
            <div className="space-y-2">
              {form.slots.map((slot, idx) => (
                <div key={slot.slot_id} className="flex items-center gap-2">
                  <Input
                    type="datetime-local"
                    value={slot.start_iso}
                    onChange={(e) => updateSlot(idx, { start_iso: e.target.value })}
                    className="rounded-xl text-sm h-9 flex-1"
                    data-testid={`host-invites-slot-start-${idx}`}
                  />
                  <Input
                    type="datetime-local"
                    value={slot.end_iso}
                    onChange={(e) => updateSlot(idx, { end_iso: e.target.value })}
                    className="rounded-xl text-sm h-9 flex-1"
                    data-testid={`host-invites-slot-end-${idx}`}
                  />
                  <button
                    onClick={() => removeSlot(idx)}
                    disabled={form.slots.length === 1}
                    className="text-stone-400 hover:text-red-500 disabled:opacity-30"
                    data-testid={`host-invites-slot-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addSlot}
              className="text-xs text-stone-600 hover:text-stone-900 mt-2 flex items-center gap-1"
              data-testid="host-invites-slot-add"
            >
              <Plus className="w-3 h-3" /> Add another slot
            </button>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleCreate} className="flex-1 h-9 rounded-full text-sm" data-testid="host-invites-create-btn">
              Create invite
            </Button>
            <Button onClick={() => setShowForm(false)} variant="outline" className="h-9 rounded-full text-sm">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-stone-500">{t("host.invites.loading", "Loading...")}</p>
      ) : invites.length === 0 ? (
        <p className="text-xs text-stone-500" data-testid="host-invites-empty">
          {t("host.invites.empty", "No invites yet. Create one and DM the link to your client.")}
        </p>
      ) : (
        <div className="space-y-2" data-testid="host-invites-list">
          {invites.map((inv) => (
            <div
              key={inv.invite_id}
              className="p-3 border border-stone-200 rounded-xl flex items-center justify-between"
              data-testid={`host-invite-row-${inv.invite_id}`}
            >
              <div className="min-w-0 flex-1 pr-2">
                <p className="text-sm font-medium text-stone-900 truncate">
                  {inv.sent_to_name || "Personal invite"}
                  {inv.service_label ? ` · ${inv.service_label}` : ""}
                </p>
                <p className="text-[11px] text-stone-500">
                  {(inv.slots || []).length} slot{inv.slots?.length === 1 ? "" : "s"} · {inv.status}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleCopy(inv.token)}
                  className="text-xs px-2 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700 flex items-center gap-1"
                  data-testid={`host-invite-copy-${inv.invite_id}`}
                >
                  {copiedToken === inv.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedToken === inv.token ? "Copied" : "Copy link"}
                </button>
                {inv.status === "open" && (
                  <button
                    onClick={() => handleDelete(inv.invite_id)}
                    className="text-stone-400 hover:text-red-500"
                    data-testid={`host-invite-cancel-${inv.invite_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
